"use client"

import { BffApiClientMolecule } from "@/app/api-client/store"
import { getHighestCollectionOffersQueryParams } from "@/app/collections/offers/utils"
import { PriceQuote } from "@/app/nft/price-quote/price-quote"
import { NftSelectionMoleculeReatom } from "@/app/nft/sell/selection/nft-selection-molecule.reatom"
import { useSolanaServiceFee } from "@/app/sdk/action-molecules/eclipse/fees-molecule"
import { useSolanaAcceptCollectionBid } from "@/app/sdk/action-molecules/solana/solana-accept-collection-bid-molecule"
import { Trans } from "@lingui/react/macro"
import {
  Button,
  DialogClose,
  DialogHeader,
  DialogTitle,
} from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { useQuery } from "@tanstack/react-query"
import { useMolecule } from "bunshi/react"
import { useEffect, useMemo } from "react"
import { CollectionMoleculeReatom } from "../../store/index.reatom"
import { useCollectionBlockchain } from "../../store/use-collection"
import {
  NftSellOfferMoleculeReatom,
  SellCollectionNftMoleculeReatom,
} from "../use-collection-nfts-sell.reatom"
import { NftForSellSelector } from "./nft-selector/nft-for-sell-selector"
import { NftSingleSell } from "./nft-single-sell"

interface Props {
  isMultiple?: boolean
}

function useCollectionOffersSync() {
  const { setCollectionOffers } = useMolecule(SellCollectionNftMoleculeReatom)
  const { collectionId } = useMolecule(CollectionMoleculeReatom)
  const bffApiClient = useMolecule(BffApiClientMolecule)

  const offersQuery = useQuery({
    ...getHighestCollectionOffersQueryParams(bffApiClient, collectionId),
  })

  useEffect(() => {
    if (offersQuery.data) {
      setCollectionOffers(collectionId, {
        isLoading: offersQuery.isLoading,
        data: offersQuery.data,
      })
    }
  }, [
    offersQuery.data,
    offersQuery.isLoading,
    setCollectionOffers,
    collectionId,
  ])
}

export const SolanaModernSellDialogContent = reatomComponent<Props>(
  ({ isMultiple }: Props) => {
    useCollectionOffersSync()

    const { sellPrice } = useMolecule(SellCollectionNftMoleculeReatom)
    const sellPriceValue = sellPrice()
    const blockchain = useCollectionBlockchain()
    const { mutate } = useSolanaAcceptCollectionBid()
    const { hasOfferToMatch } = useMolecule(NftSellOfferMoleculeReatom)
    const hasOffer = hasOfferToMatch()

    const { selectedNftsAtom } = useMolecule(NftSelectionMoleculeReatom)
    const selectedNfts = selectedNftsAtom()
    const [nft] = selectedNfts

    const title = isMultiple ? (
      hasOffer ? (
        <Trans>Select items to sell</Trans>
      ) : (
        <Trans>No offers left</Trans>
      )
    ) : (
      <Trans>Accept Offer</Trans>
    )

    const buttonText = isMultiple ? (
      <Trans>Sell now</Trans>
    ) : (
      <Trans>Accept Offer</Trans>
    )
    const buttonDisabled = sellPriceValue === undefined || nft === undefined
    const safeSellPrice = useMemo(() => sellPriceValue, [sellPriceValue])
    const solanaServiceFeeQuery = useSolanaServiceFee(blockchain, safeSellPrice)

    return (
      <form className="flex flex-col gap-y-2">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-center text-lg">{title}</DialogTitle>
        </DialogHeader>

        {isMultiple ? (
          <NftForSellSelector />
        ) : nft && safeSellPrice ? (
          <NftSingleSell nft={nft} sellPrice={safeSellPrice} />
        ) : null}

        {nft?.id !== undefined && safeSellPrice ? (
          <PriceQuote
            nftId={nft.id}
            price={safeSellPrice}
            serviceFeeQuery={solanaServiceFeeQuery}
          />
        ) : null}

        <DialogClose asChild>
          <Button
            className="flex-grow"
            type="button"
            onClick={() => {
              mutate()
            }}
            disabled={buttonDisabled}
          >
            {buttonText}
          </Button>
        </DialogClose>
      </form>
    )
  },
  "SolanaModernSellDialogContent",
)
